import * as React from 'react';

import { Button } from '@/hen-ui/atomics/Button';
import type { IconName } from '@/hen-ui/atomics/Icon';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';
import type {
  PillButtonColor,
  PillButtonHover,
  PillButtonPadding,
  PillButtonRounded,
  PillButtonSize,
  PillButtonVariant,
  PillButtonWeight,
} from './pill-button.options';
import { pillButtonVariants } from './pill-button.variants';

export interface PillButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'children' | 'type' | 'rel' | 'color'
  > {
  /** Words shown on the pill. */
  label: string;
  /**
   * Icon shown before the label.
   * Lucide kebab name, or a host-injected `custom/{slug}` when IconRegistry is present.
   */
  iconBefore?: IconName;
  /** Label weight. Unset uses `--cds-pill-button-font-weight`. */
  weight?: PillButtonWeight;
  /** Type size, padding and icon size. Unset uses the pill size tokens. */
  size?: PillButtonSize;
  /**
   * Fill style. Unset uses the pill color tokens unless `color` is set, which
   * implies `filled`.
   */
  variant?: PillButtonVariant;
  /** Palette color. Unset with a `variant` uses `neutral`. */
  color?: PillButtonColor;
  /**
   * Hover color of the chosen `variant` and `color`: lighter (unset), darker,
   * or none to keep the rest color. With neither set, darker uses the neutral
   * darker shade and lighter keeps the tenant's hover.
   */
  hover?: PillButtonHover;
  /** Corner shape. Unset uses `--cds-pill-button-radius`. */
  rounded?: PillButtonRounded;
  /** Space around the label. Unset uses the size's padding. */
  padding?: PillButtonPadding;
}

/**
 * Compact, fully rounded action button, for example the "See all photos"
 * control laid over a gallery image.
 *
 * Renders a hen-ui Button with no Button color or size variant, so it shares
 * Button's focus, press, disabled and busy behavior while its type, spacing,
 * shape and colors come from the `--cds-pill-button-*` tokens. Restyling the
 * pill never changes buttons. The options override those tokens for one pill.
 *
 * Always a native `type="button"`; bind a click action such as Open modal.
 *
 * @example
 * <PillButton label='See all photos' iconBefore='images' onClick={openGallery} />
 */
export const PillButton = React.forwardRef<HTMLButtonElement, PillButtonProps>(
  (
    {
      label,
      iconBefore,
      weight,
      size,
      variant,
      color,
      hover,
      rounded,
      padding,
      className,
      ...props
    },
    ref,
  ) => (
    <Button
      ref={ref}
      variant={null}
      size={null}
      type='button'
      iconBefore={iconBefore}
      data-hen-id={COMPONENT_IDS.PillButton}
      className={cn(
        pillButtonVariants({
          weight: weight || undefined,
          size: size || undefined,
          variant: variant || (color ? 'filled' : undefined),
          color: color || (variant ? 'neutral' : undefined),
          rounded: rounded || undefined,
          padding: padding || undefined,
          hover: hover || undefined,
        }),
        className,
      )}
      {...props}
    >
      {label}
    </Button>
  ),
);

PillButton.displayName = 'PillButton';
